import { useAppDispatch, useAppSelector } from 'app/hooks';
import FormInput from 'components/common/form-input';
import Button from 'components/inputs/button';
import { fetchDefaultAdminData } from 'features/admin';
import { useEffect, useState } from 'react';
import axios from 'utils/api';
import { serverErrorMessage } from 'utils/server-error-message';
import { errorModal, successModal } from 'utils/ui';

// The two knobs that turn the raw heating bin sum into an annual estimate.
// Without them the model bills every hour below the setpoint at the full
// design-load line, which ran about 1.9x the public operating-cost calculator.
//
// The API owns the defaults and the bounds (operating-cost-tunables.ts) and
// rejects an out-of-range save with the range in the message, so nothing is
// mirrored here that could drift from it.
export default function HeatingOperatingCostCalibrationSettings() {
  const dispatch = useAppDispatch();
  const settings = useAppSelector((state) => state.admin.settings);
  const [offset, setOffset] = useState<number>();
  const [correction, setCorrection] = useState<number>();

  useEffect(() => {
    if (!settings) {
      return;
    }
    setOffset(settings.heatingBalancePointOffsetF);
    setCorrection(settings.heatingDegreeDayCorrectionFactor);
  }, [settings]);

  const handleSave = async () => {
    try {
      await axios.patch('settings', [
        { key: 'heatingBalancePointOffsetF', value: String(offset) },
        { key: 'heatingDegreeDayCorrectionFactor', value: String(correction) }
      ]);
      dispatch(fetchDefaultAdminData());
      successModal('Settings updated.');
    } catch (error) {
      errorModal(serverErrorMessage(error, 'Something went wrong.'));
    }
  };

  return (
    <div className='flex flex-col gap-5'>
      <div className='text-xl font-bold text-gray-900'>
        Heating Operating Cost Calibration
      </div>
      <div className='text-gray-500'>
        Turns the raw heating bin sum into an annual estimate. The load line
        pivots on the design condition, so neither setting changes equipment
        sizing or the coverage percentages — only the estimated cost.
      </div>
      <div className='flex flex-wrap gap-8'>
        <div className='min-w-[260px]'>
          <FormInput
            type='number'
            label='Balance point offset (°F)'
            subLabel='Degrees below the heating setpoint at which the house stops needing heat. Internal gains and solar carry it over that span.'
            value={offset}
            onChange={setOffset}
            step={1}
            required
          />
        </div>
        <div className='min-w-[260px]'>
          <FormInput
            type='number'
            label='Degree-day correction factor'
            subLabel='Covers the conservatism built into Manual J design loads. ASHRAE puts the residential range near 0.6-0.7; 1 disables the correction.'
            value={correction}
            onChange={setCorrection}
            step={0.01}
            required
          />
        </div>
      </div>
      <div>
        <Button text='Save' color='green' onClick={handleSave} />
      </div>
    </div>
  );
}
